'use client';

import { useEffect, useState, useRef } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
import { ScrollArea } from '@radix-ui/react-scroll-area';
import { useLivePreview } from '@payloadcms/live-preview-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@shadcn/components/ui/tabs';
import { scrollIntoView } from 'seamless-scroll-polyfill';

import { Course, Courseversion } from '@/payload/payload-types';
import { Flatten } from '../../_utilities/typescript';

import ChevronLeft from '../../_icons/Chevron-left';
import ChevronRight from '../../_icons/Chevron-right';

import { AttentionBox } from '../attention-box';
import { CoursePlan } from '../CoursePlan';
import { TabsContentTitle } from './TabsContentTitle';
import { Button } from '../Button';

import { Prerequisites } from './Tabs/Prerequisites';
import { Lecturers } from './Tabs/Lecturers';
import { TimeManagement } from './Tabs/time-management';
import RichTextLexical from '../RichTextLexical';
import { getDiscountReduction, useTrackEvent } from '../../_providers/TrackEventProvider';

import useScrollShadow from './useScrollShadow';
import { FooterNav, FooterNavItem, TabValues } from './getFooterNavigation';
import { EventName } from '../../_providers/TrackEventProvider/EventConfig';
import { logToPayload } from '../../_utilities/log-to-payload';

export const CourseTabs: React.FC<{
	course: Course;
	courseversion: Flatten<Course['courseversions']>;
	isDraft?: boolean;
}> = props => {
	const t = useTranslations('Course');
	const locale = useLocale();
	const trackEvent = useTrackEvent();
	const { course: initialCourse, courseversion: initialCourseversion, isDraft } = props;
	// without live preview this can be a server component
	const { data } = (
		useLivePreview as <T>(args: { serverURL: string; initialData: T; depth?: number }) => {
			data: T;
		}
	)<Course>({
		serverURL: process.env.NEXT_PUBLIC_SERVER_URL || '',
		initialData: initialCourse,
		depth: 3,
	});
	const { scrollRef, isScrolledLeft, isScrolledRight } = useScrollShadow();

	const courseversion = !isDraft
		? initialCourseversion
		: data.courseversions?.find(
				_courseversion =>
					(_courseversion as Courseversion)?.id ===
					(initialCourseversion as Courseversion)?.id,
			) || data.courseversions?.[0];
	const course = data;

	const [activeTab, setActiveTab] = useState<TabValues>('insights');
	const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
	const tabsContentRef = useRef<HTMLDivElement>(null);
	const overviewTabRef = useRef<HTMLElement>(null);
	const topicsTabRef = useRef<HTMLElement>(null);
	const learningOutcomesTabRef = useRef<HTMLElement>(null);
	const calendarTabRef = useRef<HTMLElement>(null);
	const prerequisitesTabRef = useRef<HTMLElement>(null);
	const instructorsTabRef = useRef<HTMLElement>(null);
	const reviewsTabRef = useRef<HTMLElement>(null);

	const tabRefs = {
		insights: overviewTabRef,
		topics: topicsTabRef,
		learningOutcomes: learningOutcomesTabRef,
		timeManagement: calendarTabRef,
		prerequisites: prerequisitesTabRef,
		lecturer: instructorsTabRef,
		reviews: reviewsTabRef,
	};

	useEffect(() => {
		if (tabRefs[activeTab]?.current && tabsContentRef?.current && !isInitialLoad) {
			scrollIntoView(tabsContentRef.current, { behavior: 'smooth' });
			// tabsContentRef.current!.scrollIntoView({ behavior: 'smooth' });

			//https://stackoverflow.com/questions/66586455/why-doesnt-scrollintoview-work-on-two-simultaneous-elements

			setTimeout(() => {
				scrollIntoView(tabRefs[activeTab].current!, {
					behavior: 'smooth',
					block: 'nearest',
					inline: 'center',
				});
			}, 300);
		} else {
			setIsInitialLoad(false);
		}
	}, [activeTab]);
	if (!courseversion || typeof courseversion === 'string') {
		if (isDraft) {
			return (
				<AttentionBox
					className="mt-10"
					text={t('kursusele-versiooni-lisamine-teeb-siinse-sisu-naehtavaks')}
				></AttentionBox>
			);
		}
		logToPayload('Course tabs not available because of courseversion', {
			courseversionId: courseversion,
			courseTitle: course?.title,
			courseId: course.id,
			locale,
		});

		return <div>No available courses</div>;
	}

	const getFooterNavigation = (course: Course, courseversion: Courseversion): FooterNav => {
		// Define all possible tabs and their content checks
		const allTabs: Array<{
			key: TabValues;
			text: string;
			contentCheck: (course: Course, courseversion: Courseversion) => boolean;
			trackEventName: EventName;
		}> = [
			{
				key: 'insights',
				text: t('insights'),
				contentCheck: (course: Course) => Boolean(course.insights?.length),
				trackEventName: 'overview_section_clicked' as EventName,
			},
			{
				key: 'topics',
				text: t('course-topics'),
				contentCheck: (_: Course, courseversion: Courseversion) =>
					Boolean(courseversion.plan.topics),
				trackEventName: 'topics_section_clicked' as EventName,
			},
			{
				key: 'learningOutcomes',
				text: t('learning-outcomes'),
				contentCheck: (course: Course) => Boolean(course.outcomes?.length),
				trackEventName: 'learning_outcomes_section_clicked' as EventName,
			},
			{
				key: 'timeManagement',
				text: t('time-management'),
				contentCheck: (_: Course, courseversion: Courseversion) =>
					Boolean(courseversion.timeEdit?.calendar?.length),
				trackEventName: 'calendar_section_clicked' as EventName,
			},
			{
				key: 'prerequisites',
				text: t('prerequisites'),
				contentCheck: (course: Course) => Boolean(course.prerequisitesGroup),
				trackEventName: 'prerequisites_section_clicked' as EventName,
			},
			{
				key: 'lecturer',
				text: t('lecturer', { count: courseversion.lecturers.length }),
				contentCheck: (_: Course, courseversion: Courseversion) =>
					Boolean(courseversion.lecturers.length),
				trackEventName: 'instructors_section_clicked' as EventName,
			},
			{
				key: 'reviews',
				text: t('reviews'),
				contentCheck: (course: Course) => Boolean(course.reviews?.length),
				trackEventName: 'reviews_section_clicked' as EventName,
			},
		];

		// Filter tabs based on content availability
		const availableTabs = allTabs.filter(tab => tab.contentCheck(course, courseversion));

		// Dynamically build the navigation object
		return availableTabs.reduce((acc, tab, index) => {
			const leftTab = index > 0 ? availableTabs[index - 1] : null;
			const rightTab = index < availableTabs.length - 1 ? availableTabs[index + 1] : null;

			acc[tab.key] = {
				left: leftTab
					? {
							value: leftTab.key,
							text: leftTab.text,
							trackEventName: leftTab.trackEventName,
						}
					: null,
				right: rightTab
					? {
							value: rightTab.key,
							text: rightTab.text,
							trackEventName: rightTab.trackEventName,
						}
					: null,
			};

			return acc;
		}, {} as FooterNav);
	};

	const footerNavigation = getFooterNavigation(course, courseversion);
	const leftNavigation = footerNavigation[activeTab]?.left;
	const rightNavigation = footerNavigation[activeTab]?.right;

	const renderNavigationButton = (direction: 'left' | 'right', navItem: FooterNavItem | null) => {
		if (!navItem) return null;

		return (
			<Button
				onClick={() => {
					setActiveTab(navItem.value);
					trackEvent(navItem.trackEventName, {
						course_name: course.title,
						component: 'course_footer',
					});
				}}
				intent="primary"
				variant="ghost"
			>
				{direction === 'left' ? <ChevronLeft /> : null}
				{navItem.text}
				{direction === 'right' ? <ChevronRight /> : null}
			</Button>
		);
	};

	//TODO: once we're using only lexical, replace ReviewSlate component with Reviews component
	return (
		<Tabs
			value={activeTab}
			// @ts-expect-error ...
			onValueChange={(tabValue: TabValues) => setActiveTab(tabValue)}
			defaultValue="insights"
			className="relative flex flex-col lg:max-w-[586px] lg:flex-1 lg:px-6 xl:max-w-[800px]"
		>
			<TabsList className="sticky top-0 z-2 h-12 justify-start rounded-none bg-white pt-24 lg:h-[160px]">
				<ScrollArea
					ref={scrollRef}
					className="scrollbar-hide overflow-x-auto bg-white whitespace-nowrap"
				>
					{Boolean(course.insights?.length) && (
						<span ref={overviewTabRef}>
							<TabsTrigger
								onClick={() =>
									trackEvent('overview_section_clicked', {
										course_name: course.title,
										component: 'course_tabs',
										course_discount_value_eur:
											getDiscountReduction(courseversion),
									})
								}
								value="insights"
							>
								{t('insights')}
							</TabsTrigger>
						</span>
					)}
					{/* TODO WIP */}
					{Boolean(courseversion.plan.topics) && (
						<span ref={topicsTabRef}>
							<TabsTrigger
								onClick={() =>
									trackEvent('topics_section_clicked', {
										course_name: course.title,
										component: 'course_tabs',
										course_discount_value_eur:
											getDiscountReduction(courseversion),
									})
								}
								value="topics"
							>
								{t('course-topics')}
							</TabsTrigger>
						</span>
					)}
					{Boolean(course.outcomes?.length) && (
						<span ref={learningOutcomesTabRef}>
							<TabsTrigger
								onClick={() =>
									trackEvent('learning_outcomes_section_clicked', {
										course_name: course.title,
										component: 'course_tabs',
										course_discount_value_eur:
											getDiscountReduction(courseversion),
									})
								}
								value="learningOutcomes"
							>
								{t('learning-outcomes')}
							</TabsTrigger>
						</span>
					)}
					{Boolean(courseversion?.timeEdit?.calendar?.length) && (
						<span ref={calendarTabRef}>
							<TabsTrigger
								onClick={() =>
									trackEvent('calendar_section_clicked', {
										course_name: course.title,
										component: 'course_tabs',
										course_discount_value_eur:
											getDiscountReduction(courseversion),
									})
								}
								value="timeManagement"
							>
								{t('time-management')}
							</TabsTrigger>
						</span>
					)}

					{course?.prerequisitesGroup && (
						<span ref={prerequisitesTabRef}>
							<TabsTrigger
								onClick={() =>
									trackEvent('prerequisites_section_clicked', {
										course_name: course.title,
										component: 'course_tabs',
										course_discount_value_eur:
											getDiscountReduction(courseversion),
									})
								}
								value="prerequisites"
							>
								{t('prerequisites')}
							</TabsTrigger>
						</span>
					)}
					{
						<span ref={instructorsTabRef}>
							<TabsTrigger
								onClick={() =>
									trackEvent('instructors_section_clicked', {
										course_name: course.title,
										component: 'course_tabs',
										course_discount_value_eur:
											getDiscountReduction(courseversion),
									})
								}
								value="lecturer"
							>
								{t('lecturer', { count: courseversion.lecturers.length })}
							</TabsTrigger>
						</span>
					}
					{Boolean(course.reviews?.length) && (
						<span ref={reviewsTabRef}>
							<TabsTrigger
								onClick={() =>
									trackEvent('reviews_section_clicked', {
										course_name: course.title,
										component: 'course_tabs',
										course_discount_value_eur:
											getDiscountReduction(courseversion),
									})
								}
								value="reviews"
							>
								{t('reviews')}
							</TabsTrigger>
						</span>
					)}
				</ScrollArea>

				{/* Conditionally render shadows based on scroll position */}
				{!isScrolledLeft && (
					<div className="pointer-events-none absolute top-[64px] left-0 h-12 w-[100px] bg-linear-to-l from-transparent to-white lg:top-[108px] lg:w-40"></div>
				)}
				{!isScrolledRight && (
					<div className="pointer-events-none absolute top-[64px] right-0 h-12 w-[100px] bg-linear-to-r from-transparent to-white lg:top-[108px] lg:w-40"></div>
				)}
			</TabsList>
			<div ref={tabsContentRef} id="tabs-content" className="scroll-mt-40 lg:scroll-mt-28">
				<TabsContent value="insights">
					<TabsContentTitle title={t('insight-to-course')} />

					{course.insights?.map((insight, i) => {
						return (
							<div key={`insight-${insight.id || i}`} className="mb-12">
								<h3 className="text-gold-light mb-4 text-xl font-bold">
									{insight.insightTitleRichTextLexical && (
										<RichTextLexical
											content={insight.insightTitleRichTextLexical}
										/>
									)}
								</h3>
								{insight.insightTextRichTextLexical && (
									<RichTextLexical content={insight.insightTextRichTextLexical} />
								)}
							</div>
						);
					})}
					{course.goalDescriptionRichTextLexical && (
						<>
							<h3 className="text-gold-light mb-4 text-xl font-bold">
								{t('course-goals')}
							</h3>
							<RichTextLexical content={course.goalDescriptionRichTextLexical} />
						</>
					)}
				</TabsContent>
				{courseversion.timeEdit.calendar?.length && (
					<TabsContent value="timeManagement">
						<TimeManagement course={course} courseversion={courseversion} />
					</TabsContent>
				)}

				<TabsContent value="topics">
					<TabsContentTitle title={t('course-topics-title')} />

					<div className="space-y-8">
						{courseversion.plan.setupDescriptionRichTextLexical && (
							<RichTextLexical
								content={courseversion.plan.setupDescriptionRichTextLexical}
							/>
						)}

						<CoursePlan
							courseversion={courseversion}
							planItems={courseversion.plan?.topics}
							periodChoice={courseversion.plan.periodChoice}
						/>
					</div>
				</TabsContent>

				<TabsContent value="learningOutcomes">
					<TabsContentTitle title={t('learning-outcomes-title')} />

					<div className="space-y-4">
						<p className="text-muted-navy pb-2 font-bold">
							{t('course-participant-can')}
						</p>

						<ul className="space-y-4">
							{course.outcomes?.map((outcome, i) => {
								return (
									<li
										key={`outcome-${outcome.id || i}`}
										className="flex items-center"
									>
										{outcome.outcomeRichTextLexical && (
											<RichTextLexical
												content={outcome.outcomeRichTextLexical}
											/>
										)}
									</li>
								);
							})}
						</ul>

						{course.passingRequirementsDescriptionRichTextLexical && (
							<div className="space-y-4">
								<h3 className="text-gold-light mt-12 text-xl font-bold">
									{t('passing-requirements')}
								</h3>
								<RichTextLexical
									content={course.passingRequirementsDescriptionRichTextLexical}
								/>
							</div>
						)}
					</div>
				</TabsContent>
				<TabsContent value="prerequisites">
					<TabsContentTitle title={t('prerequisites')} />
					<Prerequisites course={course} />
				</TabsContent>
				<TabsContent value="lecturer">
					<Lecturers course={course} courseversion={courseversion} />
				</TabsContent>
				<TabsContent value="reviews">
					<TabsContentTitle title={t('reviews')} />
					<div className="space-y-6">
						{course?.reviews?.map((review, i) => (
							<ReviewSlate
								key={`review-${review.id || i}`}
								review={review}
							></ReviewSlate>
						))}
					</div>
				</TabsContent>
			</div>

			<div className="flex w-full items-center justify-center py-20">
				<div className="flex space-x-20">
					{renderNavigationButton('left', leftNavigation)}
					{renderNavigationButton('right', rightNavigation)}
				</div>
			</div>
		</Tabs>
	);
};

export const ReviewSlate: React.FC<{ review: Flatten<Course['reviews']> }> = ({ review }) => {
	if (!review) {
		return null;
	}
	return (
		<div className="border-gold-light/30 rounded-2xl border p-8">
			<div className="mb-6 flex">
				<Image
					alt={review.name}
					className="mr-2 rounded-full"
					height={48}
					src={'/assets/avatar.jpg'}
					width={48}
					style={{ objectFit: 'cover', height: '48px' }}
				/>
				<div className="flex flex-col justify-center">
					<div>
						<span className="ml-3 font-bold">{review.name}</span>

						<span className="text-brand-medium-grey ml-2 font-normal capitalize">
							{review.role}
						</span>
					</div>
					{review.year && <p className="text-gold ml-3 text-sm">{review.year}</p>}
				</div>
			</div>
			<RichTextLexical content={review.textRichTextLexical} />
		</div>
	);
};
